import { useEffect, useRef } from 'react';

import { parseAbi } from 'viem';
import { useReadContract, type UseReadContractParameters } from 'wagmi';

import { NATIVE_EVM_CURRENCY_TO_CHAIN_ID } from '@/constants';
import { normalizeEvmAddress } from '@/networks/evm';
import { ERC20Currency, EvmCurrency, Trc20Currency, TronCurrency } from '@/types';
import { isEvmCurrency, isTronCurrency } from '@/utils';

const abiString = import.meta.env.VITE_L1_PAID_FOR_ABI;
const PAID_FOR_ABI = abiString && parseAbi([abiString]);

type UsePaidForParameters = {
  contractAddress: string;
  itemId: string;
  recipient: string;
  enabled?: boolean;
  token: EvmCurrency | ERC20Currency | TronCurrency | Trc20Currency;
};

export const useEvmPaidFor = ({ contractAddress, itemId, recipient, enabled, token }: UsePaidForParameters) => {
  const chainNativeToken =
    isEvmCurrency(token) || isTronCurrency(token) ? token : (token.split('_')[1] as EvmCurrency | TronCurrency);

  const chainId = NATIVE_EVM_CURRENCY_TO_CHAIN_ID[chainNativeToken];

  const contractParams: UseReadContractParameters = {
    chainId,
    address: normalizeEvmAddress(contractAddress, token),
    abi: PAID_FOR_ABI,
    functionName: 'paidFor',
    args: [BigInt(itemId), recipient],
    query: {
      enabled: !!PAID_FOR_ABI && enabled,
    },
  };

  const { data, refetch, isFetched, isLoading } = useReadContract(contractParams);

  const isFetchedRef = useRef(isFetched);
  isFetchedRef.current = isFetched;

  useEffect(() => {
    if (isFetchedRef.current) {
      void refetch();
    }
  }, [contractAddress, itemId, recipient]);

  return isLoading || !data ? null : (data as bigint);
};
